import React, { forwardRef, useMemo } from 'react';
import { OverlayScrollbarsComponent } from 'overlayscrollbars-react';
import 'overlayscrollbars/overlayscrollbars.css';

// macOS WKWebView ignores ::-webkit-scrollbar styling whenever the system uses
// overlay scrollbars: the bar looks fine while scrolling but reverts to the wide
// native scrollbar the moment the pointer hovers it. OverlayScrollbars renders the
// scrollbar as DOM nodes instead, so it can't be hijacked by the native one.
// Theme (colors/size) lives in index.css under `.os-theme-apiboo`.
/**
 * The app's scrollbar options — also used by the imperative OverlayScrollbars
 * instances on injected HTML (aiChat/useOverlayScrollBlocks), so the two can
 * never drift apart.
 */
export const OS_SCROLL_OPTIONS = {
  scrollbars: {
    theme: 'os-theme-apiboo',
    autoHide: 'leave',
    autoHideDelay: 0,
    clickScroll: true,
  },
};

const OsScroll = forwardRef(function OsScroll({ options, children, ...rest }, ref) {
  // Memoised: a fresh object each render makes the library re-apply its
  // options (and re-read styles) on every re-render of the parent.
  const merged = useMemo(
    () => ({
      ...options,
      scrollbars: {
        theme: OS_SCROLL_OPTIONS.scrollbars.theme,
        autoHide: OS_SCROLL_OPTIONS.scrollbars.autoHide,
        // **Zero.** The bar goes the moment the pointer leaves. 400 read as
        // stuck and 150 still read as slow, and the delay was never the whole
        // story: the library ALSO fades over 0.15s, so the two compounded.
        // The fade is shortened alongside this in `.os-theme-apiboo`
        // (index.css) — changing one without the other barely moves what the
        // user actually perceives. One value for the whole app; a per-surface
        // override is how two scrollers end up disagreeing.
        autoHideDelay: OS_SCROLL_OPTIONS.scrollbars.autoHideDelay,
        clickScroll: OS_SCROLL_OPTIONS.scrollbars.clickScroll,
        ...(options && options.scrollbars),
      },
      }),
    [options]
  );
  return (
    <OverlayScrollbarsComponent
      ref={ref}
      defer
      options={merged}
      {...rest}
    >
      {children}
    </OverlayScrollbarsComponent>
  );
});

export default OsScroll;
